import type { FC, ReactText, KeyboardEvent } from 'react';
import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { codes as key } from 'keycode';

import type { TRadioTags } from './models';

export const RadioTags: FC<TRadioTags> = ({
    Tag,
    Text,
    asRadioInput,
    bordered = false,
    caption = '',
    className,
    error = false,
    errorCaption = 'Обязательное поле',
    forwardedRef,
    name,
    onSelect = /* istanbul-ignore-next */ () => {},
    renderInput = false,
    selected,
    size = 'm',
    styles,
    tags = [],
    titleClassName,
    theme = 'white',
    mapTagSizeToViewTextV2,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const [current, setCurrent] = useState(0);
    const classNames = cn(styles.radioTags, className, {
        [styles.error]: error,
    });
    const captionClassNames = cn(styles.external, styles.caption, {
        [styles.captionError]: error,
    });

    const textComponentClassNames = cn(
        {
            [styles.external]: size === 'xs',
            [styles.marginBottom12]: size === 'xs',
        },
        titleClassName
    );

    const getHandlerOnSelect = (disabled?: boolean) => (value: ReactText) => {
        if (disabled) return;

        onSelect({ value, name });
    };
    // TODO: следующие два блока не влияют на выбранные элементы. Нужно выяснить нужны ли эти блоки, и если да то дописать реализацию
    /* istanbul ignore next */
    const refocus = () =>
        setTimeout(() => {
            if (forwardedRef?.current) forwardedRef.current.focus();
        }, 0);
    /* istanbul ignore next */
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!asRadioInput) return;
        const max = tags.length - 1;

        switch (event.keyCode) {
            case key.left:
            case key.up:
                event.preventDefault();
                setCurrent(current > 0 ? current - 1 : max);
                refocus();
                break;
            case key.down:
            case key.right:
                event.preventDefault();
                setCurrent(current < max ? current + 1 : 0);
                refocus();
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        setCurrent(
            Math.max(
                tags.findIndex(val => val.value === selected),
                0
            )
        );
    }, [selected, tags]);

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={classNames} onKeyDown={onKeyDown}>
            {renderInput && <input name={name} type="hidden" value={selected} />}
            <Text
                className={textComponentClassNames}
                dataTestId={`${name}-caption`}
                kind="caption"
                view={mapTagSizeToViewTextV2[size]}
            >
                {caption}
            </Text>

            {tags &&
                tags.map((tag, index) => (
                    <Tag
                        key={tag.value}
                        bordered={bordered}
                        dataTestId={`${name}-${tag.value}`}
                        disabled={tag.disabled}
                        forwardedRef={index === current ? forwardedRef : undefined}
                        label={tag.label}
                        onClick={getHandlerOnSelect(tag.disabled)}
                        selected={selected}
                        size={size}
                        tabIndex={asRadioInput && index !== current ? -1 : undefined}
                        value={tag.value}
                    />
                ))}

            {error && (
                <Text
                    className={captionClassNames}
                    dataTestId={`${name}-error-caption`}
                    kind="red"
                    theme={theme}
                    view={mapTagSizeToViewTextV2[size]}
                >
                    {errorCaption}
                </Text>
            )}
        </div>
    );
};
